import React, { useEffect, useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { useTranslation } from 'react-i18next';
import { IParts, useGetPartTypesQuery } from 'thunks/RTKQuery/parts';
import { Table } from 'components/Table';
import {
  TableChangeType,
  SortOrder,
  ColumnDescription,
} from 'react-bootstrap-table-next';
import { FilterRule } from 'components/Filter/useQueryBuilder';
import FilterBuilder, {
  ColumnType,
  FilterColumn,
  FilterColumnOption,
} from 'components/Filter/FilterBuilder';
import { confirmationSlice } from 'slices';
import { Confirmation } from 'components/Modal';
import { DropDownIcon, DropDownReverseIcon } from 'icons';
import { columns } from './columns';
import { Actions } from './Actions';
import { PartsDetailView } from './DetailView';

export interface ISortBy {
  order: SortOrder;
  dataField: string;
}
interface IPartsViewProps {
  parts: IParts[];
  count: number;
  page: number;
  sizePerPage: number;
  setPage: (page: number) => void;
  setSortBy: (sortBy: ISortBy) => void;
  sortBy: ISortBy;
  filters: FilterRule[];
  setFilters: React.Dispatch<React.SetStateAction<FilterRule[]>>;
  redoPart: (id: number) => void;
}

const COLUMN_TYPES: Record<string, ColumnType> = {
  isPrepared: 'boolean',
  isLocked: 'boolean',
  created_at: 'date',
  updated_at: 'date',
  height: 'number',
  partType: 'select',
};

const NON_FILTERABLE_FIELDS = new Set(['logo', 'actions']);

const convertToFilterColumns: any = (
  tableColumns: ColumnDescription<any, any>[],
  columnTypes: Record<string, ColumnType> = {},
  columnOptions: Record<string, FilterColumnOption[]> = {}
): FilterColumn[] => {
  // if col is hidden then dont add
  const visibleColumns = tableColumns.filter(
    (col) => !col.hidden && !NON_FILTERABLE_FIELDS.has(col.dataField)
  );
  return visibleColumns.map((col) => ({
    field: col.dataField,
    label: col.text,
    type: columnTypes[col.dataField] || 'string', // Defaults to 'string'
    options: columnOptions[col.dataField],
  }));
};

const PartsView: React.FC<IPartsViewProps> = ({
  parts,
  count,
  page,
  sizePerPage,
  setPage,
  setSortBy,
  sortBy,
  filters,
  setFilters,
  redoPart,
}: IPartsViewProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { show: showConfirmation } = useAppSelector(
    (state) => state.confirmation
  );
  const [expandedRowId, setExpandedRowId] = useState<number | null>(null);
  const { data: partTypesResponse } = useGetPartTypesQuery();
  const partTypes = partTypesResponse?.data || [];
  const filterOptions = useMemo(
    () => ({
      partType: (partTypes || []).map((pt) => ({
        value: pt.typeKey,
        label: pt.displayText,
      })),
    }),
    [partTypes]
  );

  const handleRedo = (partObj: IParts) => {
    if (partObj.id) {
      dispatch(
        confirmationSlice.actions.show({
          title: t('partsView.actions.title'),
          message: t('partsView.actions.message'),
          buttonOk: t('partsView.actions.buttonOk'),
          buttonCancel: t('partsView.actions.buttonCancel'),
          callback: async () => {
            dispatch(confirmationSlice.actions.close());
            redoPart(partObj.id);
          },
        })
      );
    }
  };

  const addActionsToTable = () => {
    columns[columns.length - 1].formatter = (cell: any, partObj: IParts) => {
      return Actions({
        parts: partObj,
        showActions: true,
        onRedo: handleRedo,
      });
    };
  };

  useEffect(() => {
    addActionsToTable();
  }, []);

  return (
    <>
      <FilterBuilder
        columns={convertToFilterColumns(columns, COLUMN_TYPES, filterOptions)}
        filters={filters}
        setFilters={setFilters}
      />

      <div className="parts-view-table">
        <Table
          key={`table-${parts.length}`}
          keyField="id"
          data={parts}
          columns={columns}
          page={page}
          sizePerPage={sizePerPage}
          totalSize={count}
          defaultSorted={[sortBy]}
          wrapperClasses="table-dealer-ticket-content"
          expandRow={{
            renderer: (row: IParts) => <PartsDetailView partId={row.id} />,
            showExpandColumn: true,
            expandByColumnOnly: true,
            expandColumnPosition: 'right',
            expanded: expandedRowId !== null ? [expandedRowId] : [],
            onExpand: (row: IParts, isExpanded: boolean) => {
              setExpandedRowId(isExpanded ? row.id : null);
            },
            expandColumnRenderer: ({ expanded }: { expanded: boolean }) =>
              expanded ? <DropDownReverseIcon /> : <DropDownIcon />,
          }}
          onTableChange={(type: TableChangeType, state) => {
            if (type === 'pagination') {
              setPage(state.page);
            }
            if (type === 'sort') {
              if (
                state.sortField !== sortBy.dataField ||
                state.sortOrder !== sortBy.order
              ) {
                setSortBy({
                  dataField: state.sortField,
                  order: state.sortOrder,
                });
              }
            }
          }}
        />
      </div>
      {showConfirmation && <Confirmation />}
    </>
  );
};

export default PartsView;
